/**
 * 任務監控（Tasks）的差異計算：「一行一筆」的清單欄位改為列出新增／刪除的項目
 *
 * json-diff 對換行字串只會輸出整串 { __old, __new }，看不出增減了哪幾筆。
 * 這裡對清單欄位改寫為 { __added: [...], __removed: [...] }。
 *
 * 規則與後台 view changes（rcm-webback libs/utils/diffListFields.js）一致，兩邊須同步維護。
 * 切行規則與產檔端一致（rcm-deployment-openresty utils/general.py 的 splitByLineBreak）：
 * 只以 \n 切開、去掉空字串，不 trim、不處理 \r，其餘字元原樣保留。
 * 行首尾空白、行尾 \r 在產檔端是不同的規則，因此照實顯示為刪一筆、加一筆。
 */
import { diff } from "json-diff";

// 以換行分隔、語意上「一行一筆」的字串欄位
export const LIST_FIELDS = new Set([
    "wafIp",
    "wafIpExclude",
    "userAllowIp",
    "slbSourceIpPool",
    "ipWhiteList",
    "ipBlackList",
    "wafUri",
    "wafUriExclude",
    "wafHeaders",
    "wafHeadersExclude",
    "wafHeadersNotExist",
    "wafIsp",
    "wafSetReqHeaders",
    "wafSetResHeaders",
    "websocketPath",
    "cusCachepath",
    "cusCachefile",
    "cusNoncachepath",
    "cusNonachefile",
    "forceCusCachePath",
    "forceCusCacheFile",
    "cusFollowCachePath",
    "cusFollowCacheFile",
    "defCachefile",
    "defNoncachepath",
]);

// 巢狀的清單欄位：欄位名稱太通用，只在指定的父層陣列底下套用
const NESTED_LIST_FIELDS = {
    netflowIpListV2: new Set(["ips"]),
};

// json-diff 對「欄位從無到有／從有到無」輸出的 key 後綴
const ADDED_OR_DELETED_KEY = /^(.+)__(added|deleted)$/;

function isListField(field, parentField) {
    const nestedFields = NESTED_LIST_FIELDS[parentField];
    return LIST_FIELDS.has(field) || Boolean(nestedFields && nestedFields.has(field));
}

function splitByLineBreak(value) {
    return value.split("\n").filter((line) => line !== "");
}

function isOldNewString(value) {
    return (
        value !== null &&
        typeof value === "object" &&
        typeof value.__old === "string" &&
        typeof value.__new === "string"
    );
}

/**
 * 新舊值做集合差。新增依新值原順序、刪除依舊值原順序；
 * 兩邊都沒有差異（只調整順序、重複行增減、空行增減）時回傳 null。
 */
function diffLines(oldValue, newValue) {
    const oldLines = splitByLineBreak(oldValue);
    const newLines = splitByLineBreak(newValue);
    const oldSet = new Set(oldLines);
    const newSet = new Set(newLines);
    const added = Array.from(new Set(newLines.filter((line) => !oldSet.has(line))));
    const removed = Array.from(new Set(oldLines.filter((line) => !newSet.has(line))));
    if (added.length === 0 && removed.length === 0) return null;
    return { __added: added, __removed: removed };
}

// 陣列 diff 中只要有一項不是未變動的佔位項 [" "]，就代表有變動
function hasArrayChange(arrayDiff) {
    return arrayDiff.some((entry) => !(Array.isArray(entry) && entry[0] === " "));
}

function rewriteObjectDiff(objectDiff, parentField) {
    const result = {};
    Object.keys(objectDiff).forEach((key) => {
        const value = objectDiff[key];
        const addedOrDeleted = key.match(ADDED_OR_DELETED_KEY);
        if (addedOrDeleted && typeof value === "string" && isListField(addedOrDeleted[1], parentField)) {
            result[key] = splitByLineBreak(value);
            return;
        }

        if (isListField(key, parentField) && isOldNewString(value)) {
            const lineDiff = diffLines(value.__old, value.__new);
            // 只調整順序 → 該欄位不顯示
            if (lineDiff) result[key] = lineDiff;
            return;
        }

        // 欄位從無到有／從有到無的值是完整資料，不是 diff，不往下改寫
        if (addedOrDeleted) {
            result[key] = value;
            return;
        }

        if (Array.isArray(value)) {
            const rewritten = rewriteArrayDiff(value, key);
            // 內層變動全被移除（只剩未變動的佔位項）→ 該欄位不顯示
            if (hasArrayChange(rewritten)) result[key] = rewritten;
        } else if (value !== null && typeof value === "object" && !("__old" in value)) {
            const rewritten = rewriteObjectDiff(value, key);
            if (Object.keys(rewritten).length > 0) result[key] = rewritten;
        } else {
            result[key] = value;
        }
    });
    return result;
}

// 陣列的 diff 由 [op, value] 組成，只有 "~"（內容有變動）需要往下改寫
function rewriteArrayDiff(arrayDiff, parentField) {
    return arrayDiff.map((entry) => {
        const op = Array.isArray(entry) ? entry[0] : undefined;
        const value = Array.isArray(entry) ? entry[1] : undefined;
        if (op !== "~" || value === null || typeof value !== "object") return entry;
        if (Array.isArray(value)) {
            const rewritten = rewriteArrayDiff(value, parentField);
            return hasArrayChange(rewritten) ? ["~", rewritten] : [" "];
        }
        const rewritten = rewriteObjectDiff(value, parentField);
        // 物件因此沒有任何變動欄位 → 改為未變動的佔位項，由上層判斷是否整個不顯示
        return Object.keys(rewritten).length > 0 ? ["~", rewritten] : [" "];
    });
}

/**
 * @param {Array|undefined} diffData json-diff 的結果
 * @returns {Array|undefined} 改寫後的結果；沒有任何變動時回傳 undefined（與 json-diff 相同）
 */
export function diffListFields(diffData) {
    if (!Array.isArray(diffData)) return diffData;
    const result = rewriteArrayDiff(diffData);
    return hasArrayChange(result) ? result : undefined;
}

/**
 * 任務的 originData / requestData（JSON 字串）差異；表格顯示與匯出共用
 * @returns {Array|undefined} 沒有任何變動時回傳 undefined
 */
export function buildTaskDiff(originData, requestData) {
    return diffListFields(
        diff(JSON.parse(originData), JSON.parse(requestData), {
            outputKeys: ["key", "value"],
        })
    );
}



// WEBPACK FOOTER //
// ./src/libs/taskDiff.js